import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';

import { type EnhancedToolResult } from '../types';

import {
    unplannedDataFinding,
    unplannedTextFinding,
    unusedTechniqueFinding,
} from './planAdherence';
import { emptyBeatMessage, measureKineticBeats, underScaleMessage } from './kineticScale';
import { incomingCarryIds, refreshCopiesAfter, seamFindings, spliceCarries } from './seamCheck';

import type { SceneHandoff, ScenePlan } from 'js/src/data/html-design';
import { checkpointVideo } from 'js/src/data/video-data';
import type { AppDispatch } from 'js/src/libs/RootState';
import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';
import { saveRawLuminary } from 'js/src/luminary/utils';

import { lintComposition } from './lint';
import type { LintFinding } from './lint';
import type { TimedWord } from './sceneNarration';

/** Per-composition, since a long build has no later boundary at which to save earlier graphics. */
export function checkpointDesign(
    luminary: Luminary,
    dispatch: AppDispatch,
    parentActionGroup: string | undefined,
): void {
    dispatch(
        checkpointVideo({
            video_data: saveRawLuminary(luminary),
            systemTriggered: false,
            parentActionGroup,
        }),
    );
}

/**
 * The error set each design run last reported, so a repeat can be called out. Keyed per conversation
 * as well as per asset: a run that ends with errors leaves them in the document, so the next run's
 * first lint reproduces that set and must not be told it already tried this.
 */
const lastErrorSignatureByRun = new Map<string, string>();

/** The carry errors each design run's last write of a graphic reported, keyed like the above. */
const carryErrorsByRun = new Map<string, Set<string>>();

const SOFTENED_CARRY_ERROR =
    ' (A warning now, since it outlasted an attempt to fix it: fix it if you can, but keep the ' +
    'handoff either way — a jump at one cut is better than a carry dropped from every seam.)';

/**
 * Which error a carry finding is, across writes: its rule and the carry it names first, which every
 * carry message quotes. The rule alone would let a new error inherit the softening of another.
 */
function carryErrorKey({ ruleId, message }: LintFinding): string {
    return `${ruleId}|${message.match(/"([^"]+)"/)?.[1] ?? ''}`;
}

/**
 * Seam errors that already blocked the previous write of this graphic, turned into warnings. Each
 * time the agent could not resolve one, it escaped by dropping the handoffs, which loses the
 * continuity at every seam to fix one; a warning leaves the seam visible without that way out.
 */
export function softenRepeatedCarryErrors(runKey: string, findings: LintFinding[]): LintFinding[] {
    const seen = carryErrorsByRun.get(runKey) ?? new Set<string>();
    const carryErrors = findings.filter(
        (finding) => finding.severity === 'error' && finding.ruleId.startsWith('carry-'),
    );
    carryErrorsByRun.set(runKey, new Set(carryErrors.map(carryErrorKey)));
    return findings.map((finding) =>
        carryErrors.includes(finding) && seen.has(carryErrorKey(finding))
            ? { ...finding, severity: 'warning', message: finding.message + SOFTENED_CARRY_ERROR }
            : finding,
    );
}

// How many warnings to quote in full, and how many of those one rule may claim. A composition
// commonly trips 30+ warnings, so quoting them all would cost more than the edit being reported —
// but quoting none leaves the agent unable to act on any of them, or to judge whether a rule is even
// firing correctly.
const WARNINGS_QUOTED_MAX = 8;
const WARNINGS_QUOTED_PER_RULE_MAX = 2;

/**
 * A readable digest of the non-blocking findings: a message from every rule that fired, a second
 * example where the budget allows, then a count of what was left out.
 *
 * Breadth first, and that order matters. Findings arrive grouped in rule-declaration order, so filling
 * straight to the per-rule cap spends the budget on the earliest rules and leaves the later ones
 * named-only — the state quoting messages exists to replace, decided by a rule's position rather than
 * by how useful its finding is.
 */
export function summarizeWarnings(warnings: LintFinding[]): string {
    if (!warnings.length) {
        return '';
    }
    const quotedPerRule = new Map<string, number>();
    const quoted = new Set<LintFinding>();
    const fill = (perRuleLimit: number): void => {
        for (const warning of warnings) {
            if (quoted.size >= WARNINGS_QUOTED_MAX) return;
            if (quoted.has(warning)) continue;
            const alreadyQuoted = quotedPerRule.get(warning.ruleId) ?? 0;
            if (alreadyQuoted >= perRuleLimit) continue;
            quotedPerRule.set(warning.ruleId, alreadyQuoted + 1);
            quoted.add(warning);
        }
    };
    fill(1);
    fill(WARNINGS_QUOTED_PER_RULE_MAX);

    const lead =
        `${warnings.length} warning(s). None block, and each one is a decision: fix it, or — where ` +
        `the message names a data-l5-* attribute — waive it with that. Leaving one neither fixed ` +
        `nor waived is how a real defect ships.`;
    // Read out in the findings' own order, so a breadth-first selection still reads grouped by rule.
    const list = warnings
        .filter((f) => quoted.has(f))
        .map((f) => `- [${f.ruleId}] ${f.message}`)
        .join('\n');
    // The caps skip findings, so the quoted set isn't a prefix of the list — the omitted ones have to
    // be derived by exclusion or the tally names the wrong rules.
    const omittedRules = [...new Set(warnings.filter((f) => !quoted.has(f)).map((f) => f.ruleId))];
    const omitted = warnings.length - quoted.size;
    if (omitted === 0) {
        return `${lead}\n${list}`;
    }
    return `${lead}\n${list}\n- …and ${omitted} more (${omittedRules.join(', ')}).`;
}

/**
 * Writes `html` onto the asset and re-lints, formatting the findings into a tool result. Lint errors
 * are surfaced as a failure (the composition is still committed so the preview reflects the broken
 * state and the agent can fix it); warnings are noted but still succeed. Shared by the write and
 * edit tools so both apply and report identically.
 *
 * `sceneDurationMs` is the playback duration (milliseconds, our default unit) of the scene the
 * graphic is on — passed through so the reading-time lint can judge whether text stays up long
 * enough. The composition timeline is normalized, so this real duration can only come from the scene.
 *
 * `conversationId` scopes the repeated-error detection to the design run making the calls.
 *
 * `techniques` are the skills the approved plan assigned this graphic, checked here because
 * loading a skill is not applying it.
 *
 * `designSystemCss` is the video's design system, passed to the lint so the composition is judged
 * against the tokens it was authored against. Like the plan, it is never written into the
 * composition: the author pastes the block it came back as, and the lint measures what rendered.
 *
 * `narration` is the voiceover over the scene in scene seconds, so copy can be judged against when
 * it is spoken. Null for a scene with no narration.
 *
 * `seam` is the scene the graphic is on and the handoffs the design system plans across its edges,
 * so an element carried across a cut can be measured against the graphic on the other side.
 *
 * `overlaysScene` is whether the graphic is layered over the scene's other content rather than
 * owning its frame.
 *
 * `checksKineticType` holds each beat's kinetic type to the frame, for a recipe that asks for it.
 */
export async function applyHtmlAndLint(
    luminary: Luminary,
    asset: HtmlAsset,
    authored: string,
    sceneDurationMs: number,
    conversationId: string | undefined,
    plan: ScenePlan | null = null,
    techniques: readonly string[] = [],
    designSystemCss: string | null = null,
    narration: readonly TimedWord[] | null = null,
    seam: {
        scene: Scene;
        handoffs: { incoming: SceneHandoff[]; outgoing: SceneHandoff[] };
    } | null = null,
    overlaysScene = false,
    checksKineticType = false,
): Promise<EnhancedToolResult> {
    const runKey = `${conversationId ?? 'no-conversation'}:${asset.id}`;
    const splice = seam
        ? await spliceCarries(luminary, seam.scene, authored)
        : { html: authored, spliced: null };
    if ('error' in splice) {
        return { result: `Not applied: ${splice.error}`, isSuccess: false };
    }
    const { html } = splice;
    const splicedNote = splice.spliced
        ? `\n\nCopied in ${splice.spliced.ids.map((id) => `"${id}"`).join(', ')} from scene ` +
          `${splice.spliced.fromScene}, exactly as it ends there. The stored composition now holds ` +
          `the element where the placeholder was, so ${TOOL_NAMES.readHtmlAsset} before editing ` +
          `inside it.`
        : '';
    luminary.batchRun(() => {
        asset.html = html;
    });
    // Before the seam check, so it measures the graphics after this one as they now open.
    const refreshed = seam ? await refreshCopiesAfter(luminary, seam.scene) : [];
    const refreshedNote = refreshed.length
        ? `\n\nRe-copied what scene ${refreshed.join(', then ')} opens on, so it starts from this ` +
          `graphic's new ending.`
        : '';

    const lint = await lintComposition(
        asset,
        sceneDurationMs,
        designSystemCss,
        narration,
        seam ? incomingCarryIds(luminary, seam.scene, html) : undefined,
        overlaysScene,
    );
    const { renderedHtml } = lint;
    const findings = [
        ...lint.findings,
        ...(seam
            ? softenRepeatedCarryErrors(
                  runKey,
                  await seamFindings(luminary, seam.scene, asset, seam.handoffs),
              )
            : []),
    ];
    const warnings = findings.filter((finding) => finding.severity === 'warning');

    // Not Luminary rules because the plan never reaches the renderer, but reported with the lint
    // errors so they block the same way.
    const errors = findings.filter((finding) => finding.severity === 'error');
    const unplanned = unplannedTextFinding(html, plan);
    if (unplanned) {
        errors.push({ ruleId: 'text-not-in-plan', severity: 'error', message: unplanned });
    }
    const unplannedData = unplannedDataFinding(html, plan);
    if (unplannedData) {
        errors.push({ ruleId: 'data-not-in-plan', severity: 'error', message: unplannedData });
    }
    const unusedTechniques = unusedTechniqueFinding(html, techniques, renderedHtml);
    if (unusedTechniques) {
        errors.push({
            ruleId: 'technique-not-applied',
            severity: 'error',
            message: unusedTechniques,
        });
    }

    const beats = checksKineticType
        ? await measureKineticBeats(asset, plan, techniques, sceneDurationMs)
        : null;
    const underScale = beats ? underScaleMessage(beats) : null;
    if (underScale) {
        errors.push({ ruleId: 'kinetic-type-under-scale', severity: 'error', message: underScale });
    }
    const emptyBeats = beats ? emptyBeatMessage(beats) : null;
    if (emptyBeats) {
        errors.push({ ruleId: 'kinetic-beat-empty', severity: 'error', message: emptyBeats });
    }

    if (errors.length > 0) {
        const list = errors.map((f) => `- [${f.ruleId}] ${f.message}`).join('\n');
        const signature = errors.map((f) => `${f.ruleId}|${f.message}`).join('\n');
        // A composition that could not be measured says nothing about the edit, so repeating it is
        // not the author failing to change their approach — telling them it was would send them
        // rewriting markup that may be fine.
        const measured = errors.every((f) => f.ruleId !== 'composition-not-measured');
        const repeated = measured && lastErrorSignatureByRun.get(runKey) === signature;
        lastErrorSignatureByRun.set(runKey, signature);
        const lead = repeated
            ? `Applied, but all ${errors.length} lint error(s) are unchanged from your previous attempt — that edit did not affect them. Read the offending markup and change your approach instead of resending an equivalent fix:`
            : `Applied, but the composition has ${errors.length} lint error(s) that must be fixed:`;
        return {
            result: `${lead}\n${list}${splicedNote}${refreshedNote}`,
            isSuccess: false,
        };
    }

    lastErrorSignatureByRun.delete(runKey);
    const warningNote = summarizeWarnings(warnings);
    return {
        result:
            (warningNote
                ? `Successfully updated the design. ${warningNote}`
                : 'Successfully updated the design.') +
            splicedNote +
            refreshedNote,
        isSuccess: true,
    };
}
